import React, { useEffect, useState } from "react";

import { TFunction } from "i18next";
import debounce from "lodash/debounce";
import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import { ChangeEvent, Fragment, useCallback, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AtiraSelect } from "../../../components/AtiraSelect";
import {
  TableHeader,
  TABLE_SEARCH_DEBOUNCE_MS,
} from "../../../components/table/TableHeader";
import { GetLatestEntriesFilterDto } from "../../../model/entry/dto/GetLatestEntriesFilterDto";
import { EntryKind } from "../../../model/entry/types/EntryKind.enum";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { entryActions } from "../../../redux/entry/entry.slice";
import { formSliceSelectors } from "../../../redux/forms/form.selector";
import { permissionSliceSelectors } from "../../../redux/permission/permission.selector";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { Flex } from "../../../components/Flex";
import { Spacing } from "../../../theme/Spacing";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { MemberAvatar } from "../../../components/MemberAvatar";
import { faUserSlash } from "@fortawesome/free-solid-svg-icons/faUserSlash";
import { faRefresh } from "@fortawesome/free-solid-svg-icons/faRefresh";
import { useTheme } from "../../../ThemeContext";
import { rangeOptions } from "../../../utils/Date";
import { AtiraDateRange } from "../../../components/AtiraDateRange";
import { TestIDs } from "../../../tests/types/testIds";
import { DELETED_KIND_FILTER_VALUE } from "../utils/deletedKindFilter";
import { tagSliceSelectors } from "../../../redux/tag/tag.selector";
import { TagChip } from "../../../components/TagChip";

const kindOptions = (t: TFunction) => {
  return Object.entries({
    ALL_ENTRIES: null,
    ...EntryKind,
    DELETED: DELETED_KIND_FILTER_VALUE,
  }).map(([key, value]) => ({
    label: t(`common.${key.toLowerCase()}`),
    value,
    "data-testid": TestIDs.entries.tableHeader.kindFilter.option(
      value || "all",
    ),
  }));
};

const ColorDots = styled.div`
  display: flex;
  gap: ${Spacing.xs};
`;

const Dot = styled.span<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  background-color: ${({ color }) => color};
  border: 1px solid rgba(0, 0, 0, 0.08);
`;

const StyledSelect = styled(AtiraSelect)`
  flex: 1;
  width: 7.5rem;
  min-width: 7.5rem;

  .ant-select .ant-select-selector {
    border: 2px solid ${(props) => props.theme.transparent} !important;
  }
`;

const MultiValueSelect = styled(StyledSelect)`
  flex: 0 0 10rem;
  width: 10rem;
  min-width: 10rem;

  .ant-select.ant-select-multiple .ant-select-selector {
    align-items: center;
    overflow: hidden;
  }

  .ant-select.ant-select-multiple .ant-select-selection-overflow {
    flex-wrap: nowrap;
  }

  .ant-select.ant-select-multiple .ant-select-selection-overflow-item,
  .ant-select.ant-select-multiple .ant-select-selection-item {
    max-width: 100%;
  }

  .ant-select.ant-select-multiple .ant-select-selection-item-content {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ant-select-content {
    position: relative;
    align-self: center;
  }
`;

const MarkedForDeletionShape = styled(Flex)`
  width: 2rem;
  height: 1rem;
  background-image: ${({ theme }) => `
    repeating-linear-gradient(
      45deg,
      ${theme.white},
      ${theme.white} 6px,
      ${theme.sub} 6px,
      ${theme.sub} 7px
    )
    `};
  border-inline-start: 4px solid ${({ theme }) => theme.danger};
`;

const VerticalSeparator = styled(Flex)`
  height: 1.5rem;
  width: 2px;
  background-color: ${({ theme }) => theme.darkerSub};
`;

const Wrapper = styled(Flex)`
  position: sticky;
  top: 3rem;
  z-index: 100;
  background-color: ${({ theme }) => theme.sub};
  width: 100%;
  padding-inline: ${Spacing.m};
`;

type EntryTableHeaderProps = {
  showFilters: boolean;
};

export const EntryTableHeader: React.FC<EntryTableHeaderProps> = ({
  showFilters = false,
}) => {
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number>(0);

  const { getValues, setValue, control, watch } =
    useFormContext<GetLatestEntriesFilterDto>();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { theme } = useTheme();

  const onlyMarkedForDeletion = watch("onlyMarkedForDeletion");
  const unassigned = watch("unassigned");

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const forms = useAppSelector(formSliceSelectors.selectForms);
  const entriesLoading = useAppSelector(
    entrySliceSelectors.selectLatestEntriesLoading,
  );
  const entriesPageSize = useAppSelector(
    entrySliceSelectors.selectEntriesTablePageSize,
  );
  const entriesPage = useAppSelector(
    entrySliceSelectors.selectEntriesTablePage,
  );
  const members = useAppSelector(permissionSliceSelectors.selectMembers);
  const tags = useAppSelector(tagSliceSelectors.selectTags);

  const assigneeOptions = useMemo(() => {
    return [
      {
        label: t("entries.table.entry.filters.assignee.all"),
        value: null,
        "data-testid": TestIDs.entries.tableHeader.assigneeFilter.option("all"),
      },
      {
        label: t("entries.table.entry.filters.assignee.unassigned"),
        value: "UNASSIGNED",
        "data-testid":
          TestIDs.entries.tableHeader.assigneeFilter.option("UNASSIGNED"),
      },
      ...members.map((m) => ({
        label: m.name,
        value: m._id,
        "data-testid": TestIDs.entries.tableHeader.assigneeFilter.option(m._id),
      })),
    ];
  }, [members, t]);

  const renderAssignee = (v: { label?: React.ReactNode; value?: unknown }) => {
    const member = members.find((m) => m._id === v.value);

    return (
      <Flex alignItems="center" gap="xs">
        {v.value === "UNASSIGNED" ? (
          <Flex flexShrink={0}>
            <AtiraIcon icon={faUserSlash} color="darkerSub" />
          </Flex>
        ) : member ? (
          <MemberAvatar member={member} />
        ) : null}
        <span>{v.label}</span>
      </Flex>
    );
  };

  const tagOptions = useMemo(
    () =>
      tags.map((tag) => ({
        value: tag._id,
        label: tag.name,
        "data-testid": TestIDs.entries.tableHeader.tagsFilter.option(tag.name),
      })),
    [tags],
  );

  const renderTagOption = (v: { value?: unknown }) => {
    const tag = tags.find((tag) => tag._id === v.value);

    return tag ? <TagChip tag={tag} /> : null;
  };

  const formOptions = useMemo(() => {
    return forms.map((f) => ({
      value: f._id,
      label: f.orphan ? t("forms.manual_entry") : f.title,
      "data-testid": TestIDs.entries.tableHeader.formsFilter.option(f._id),
    }));
  }, [forms, t]);

  const onFetchEntries = useCallback(() => {
    dispatch(
      entryActions.getLatestEntries({
        userId,
        meta: { count: entriesPageSize || 20, page: 0 },
        ...{
          ...omitBy(getValues(), isNil),
          assigneeIds: getValues().assigneeIds?.filter(Boolean),
        },
      }),
    );
    dispatch(entryActions.getUserTableSettings({ userId }));
  }, [dispatch, entriesPageSize, getValues, userId]);

  const renderThemeOption = (v: any) => {
    return (
      <Flex alignItems="center" gap="xs" justifyContent="space-between">
        <span>{v.label}</span>
        {v.value === DELETED_KIND_FILTER_VALUE ? (
          <MarkedForDeletionShape />
        ) : (
          <ColorDots>
            <Dot
              color={theme[`${v.value?.toLowerCase()}` as keyof typeof theme]}
            />
          </ColorDots>
        )}
      </Flex>
    );
  };

  const onSearch = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setValue("keyword", target.value);
    debouncedSearch();
  };

  const onFilter = async () => {
    try {
      await onFetchEntries();
    } catch (e: any) {
      AtiraToast.apiError(e);
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce(() => {
        dispatch(entryActions.setEntriesTablePage(1));
        onFetchEntries();
      }, TABLE_SEARCH_DEBOUNCE_MS),
    [dispatch, onFetchEntries],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoRefreshInterval > 0) {
      interval = setInterval(() => {
        dispatch(
          entryActions.getLatestEntries({
            userId,
            meta: {
              count: entriesPageSize,
              page: entriesPage - 1,
            },
            ...{
              ...omitBy(getValues(), isNil),
              assigneeIds: getValues().assigneeIds?.filter(Boolean),
            },
          }),
        );
      }, autoRefreshInterval * 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [
    autoRefreshInterval,
    dispatch,
    entriesPage,
    entriesPageSize,
    getValues,
    userId,
  ]);

  return (
    <Wrapper>
      <TableHeader
        showFilters={showFilters}
        onFetch={onFetchEntries}
        onFetchLoading={entriesLoading}
        onSearch={onSearch}
        searchTestId={TestIDs.entries.tableHeader.searchFilter.input}
        filters={
          <Fragment>
            <Controller
              control={control}
              name="formsIds"
              render={({ field: { onChange, value } }) => (
                <MultiValueSelect
                  value={value ?? []}
                  options={formOptions}
                  className={TestIDs.entries.tableHeader.formsFilter.open}
                  onChange={(e) => {
                    onChange(e);
                    dispatch(entryActions.setEntriesTablePage(1));
                    onFilter();
                  }}
                  disabled={entriesLoading}
                  styles={{
                    popup: { root: { width: "25rem" } },
                  }}
                  mode="multiple"
                  showSearch={false}
                  maxTagCount={0}
                  maxTagPlaceholder={(selectedValues) =>
                    t("common.n-selected", {
                      number: selectedValues.length,
                      count: selectedValues.length,
                    })
                  }
                  placeholder={t("entries.table.entry.filters.forms.all")}
                />
              )}
            />

            <Controller
              control={control}
              name="range"
              render={({ field: { onChange, value } }) => (
                <StyledSelect
                  options={rangeOptions(t)}
                  value={{
                    label: !value
                      ? t("entries.table.entry.filters.time.all")
                      : t(
                          `entries.table.entry.filters.time.${value.toLowerCase()}`,
                        ),
                    value,
                  }}
                  onChange={(e) => {
                    onChange(e);
                    dispatch(entryActions.setEntriesTablePage(1));
                    onFilter();
                  }}
                  disabled={entriesLoading}
                />
              )}
            />

            <Controller
              control={control}
              name="scope"
              render={({ field: { onChange, value } }) => (
                <AtiraDateRange
                  className={TestIDs.entries.tableHeader.scopeFilter.open}
                  value={value}
                  onChange={(dateStrings) => {
                    onChange(dateStrings);
                    dispatch(entryActions.setEntriesTablePage(1));
                    onFilter();
                  }}
                  loading={entriesLoading}
                />
              )}
            />

            {members?.length ? (
              <Controller
                control={control}
                name="assigneeIds.0"
                render={({ field: { onChange, value } }) => (
                  <StyledSelect
                    options={assigneeOptions}
                    value={{
                      label: unassigned
                        ? t("entries.table.entry.filters.assignee.unassigned")
                        : !value
                          ? t("entries.table.entry.filters.assignee.all")
                          : members.find((m) => m._id === value)?.name,
                      value: unassigned ? "UNASSIGNED" : value,
                    }}
                    onChange={(e) => {
                      const isUnassigned = e === "UNASSIGNED";

                      onChange(isUnassigned ? null : e);
                      setValue("unassigned", isUnassigned || undefined);
                      dispatch(entryActions.setEntriesTablePage(1));
                      onFilter();
                    }}
                    disabled={entriesLoading}
                    labelRender={renderAssignee}
                    optionRender={renderAssignee}
                    popupMatchSelectWidth={false}
                    className={TestIDs.entries.tableHeader.assigneeFilter.open}
                  />
                )}
              />
            ) : null}

            {tags.length ? (
              <Controller
                control={control}
                name="tagIds"
                render={({ field: { onChange, value } }) => (
                  <MultiValueSelect
                    value={value ?? []}
                    options={tagOptions}
                    className={TestIDs.entries.tableHeader.tagsFilter.open}
                    onChange={(e) => {
                      onChange(e);
                      dispatch(entryActions.setEntriesTablePage(1));
                      onFilter();
                    }}
                    disabled={entriesLoading}
                    mode="multiple"
                    showSearch={{ optionFilterProp: "label" }}
                    optionRender={renderTagOption}
                    popupMatchSelectWidth={false}
                    maxTagCount={0}
                    maxTagPlaceholder={(selectedValues) =>
                      t("common.n-selected", {
                        number: selectedValues.length,
                        count: selectedValues.length,
                      })
                    }
                    placeholder={t("entries.table.entry.filters.tags.all")}
                  />
                )}
              />
            ) : null}

            <Controller
              control={control}
              name="kind"
              render={({ field: { onChange, value } }) => (
                <StyledSelect
                  options={kindOptions(t)}
                  value={{
                    label: onlyMarkedForDeletion
                      ? t("common.deleted")
                      : !value
                        ? t("common.all_entries")
                        : t(`common.${value.toLowerCase()}`),
                    value: onlyMarkedForDeletion
                      ? DELETED_KIND_FILTER_VALUE
                      : value,
                  }}
                  onChange={(e) => {
                    const isDeleted = e === DELETED_KIND_FILTER_VALUE;

                    onChange(isDeleted ? undefined : e);
                    setValue("onlyMarkedForDeletion", isDeleted || undefined);
                    dispatch(entryActions.setEntriesTablePage(1));
                    onFilter();
                  }}
                  disabled={entriesLoading}
                  labelRender={renderThemeOption}
                  optionRender={renderThemeOption}
                  className={TestIDs.entries.tableHeader.kindFilter.open}
                />
              )}
            />

            {/* {kind === EntryKind.CONTACT ? (
              <Controller
                control={control}
                name="priority"
                render={({ field: { onChange, value } }) => (
                  <StyledSelect
                    options={priorityOptions(t)}
                    value={{
                      label: !value
                        ? t("entries.table.entry.filters.priority.all")
                        : t(
                            `entries.table.entry.filters.priority.${value.toLowerCase()}`,
                          ),
                      value,
                    }}
                    onChange={(e) => {
                      onChange(e);
                      dispatch(entryActions.setEntriesTablePage(1));
                      onFilter();
                    }}
                    info={t("entries.table.entry.filters.priority.info")}
                    disabled={entriesLoading}
                  />
                )}
              />
            ) : null} */}

            <VerticalSeparator />

            <Flex alignItems="center" gap="xs">
              <StyledSelect
                value={autoRefreshInterval}
                onChange={(value) => setAutoRefreshInterval(Number(value))}
                options={[
                  {
                    label: t("entries.table.entry.filters.autoRefresh"),
                    value: 0,
                  },
                  ...[10, 30, 60, 120, 300, 600].map((seconds) => ({
                    label: t(
                      seconds < 60
                        ? "common.everySecond"
                        : "common.everyMinute",
                      { count: seconds < 60 ? seconds : seconds / 60 },
                    ),
                    value: seconds,
                  })),
                ]}
                disabled={entriesLoading}
              />
              {autoRefreshInterval > 0 ? (
                <AtiraIcon
                  size="xl"
                  icon={faRefresh}
                  color="main"
                  style={{ opacity: 0.7 }}
                  animations={["spin"]}
                />
              ) : null}
            </Flex>
          </Fragment>
        }
      />
    </Wrapper>
  );
};
